import * as s from "../TableItemStyled";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import SearchInput from "components/atoms/input/search/SearchInput";
import Type from "components/atoms/text/labels/Type";
import { checkStatus, convertType } from "utils/functions";
import { DashboardChartDetailTable } from "components/atoms/table/DashboardChartDetailTable";
import { TableColumnsType } from "antd";
import { postWidgetListRecorder } from "api/widgetAPI";
import AccountSelects from "../AccountSelects";
import { commonOrgColumn } from "pages/dashboard/dashboard/WidgetContainer";
import {
  PostWidgetRecorderListResponse,
  WidgetRecorderList,
} from "api/interfaces/widgetInterface.interface";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import { Layout } from "react-grid-layout";
import TableItemHeader from "../TableItemHeader";
import { Level } from "components/atoms/level/Level";
import { getLevelFullName } from "api/mappers/accountMapper";
import * as mediaQuery from "components/MediaQuery";
import { useMediaQuery } from "react-responsive";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useInfiniteQuery } from "react-query";
import { commonPaginationPerPage, TableItemName } from "utils/WidgetUtil";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { ExpandableConfig } from "antd/es/table/interface";
import RecorderListExpand from "pages/recorders/recorders/list/RecorderListExpand";
import { Recorder } from "api/interfaces/recorderInterface.interface";
import TableTitle, { TableTitleRow } from "components/blocks/table/TableTitle";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}

export default function Recorders(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [accountIds, setAccountIds] = useState<string[]>([]);
  const isMobile = useMediaQuery({ maxWidth: mediaQuery.mobileMax });
  const initialQueryInfo = {
    ...baseQueryInfo,
    pageLimit: commonPaginationPerPage,
    sortType: "mergedSystemName",
    sortDirection: SORT_DIRECTION.ASC,
    type: null,
  };
  const [recorderQueryInfo, setRecorderQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const { pageNum, ...recorderQueryKey } = useQueryKey(recorderQueryInfo);
  const onClickRecorderType = useCallback((value: string | undefined) => {
    setRecorderQueryInfo((info) => ({
      ...info,
      type: value === "all" ? null : value,
    }));
  }, []);

  const columns: TableColumnsType<WidgetRecorderList> = useMemo(
    () => [
      {
        ...commonOrgColumn(selectedAccount),
        render: (value, row) => (
          <s.ColumnText>
            <Level level={getLevelFullName(LEVEL_TYPE.EU)} />{" "}
            <s.OrgText>{row.recorders[0].accountName}</s.OrgText>
          </s.ColumnText>
        ),
      },

      {
        title: (
          <TableColumnFilterTitle
            title="Type"
            filters={AlertRecorerTypeFilter}
            onClick={onClickRecorderType}
            data-testid="dashboard-dashboard-list-table-column-filter-title-click-recorder-type"
          />
        ),

        key: "type",
        dataIndex: "type",
        width: isMobile ? 70 : 80,
        render: (value, row) => (
          <Type
            type={convertType(row)}
            licenseType={row.licenseType}
            isCloudCam={row.isCloudCam}
          />
        ),
      },
      {
        title: "Title",
        dataIndex: "title",
        render: (value, row) => (
          <TableTitle
            row={row as unknown as TableTitleRow}
            cloudStatus={
              row.cloudSystemId !== undefined && row.cloudSystemId !== ""
            }
          />
        ),
      },
      {
        title: "Version",
        dataIndex: "firmwareVersion",
        key: "firmwareVersion",
        render: (value, row) => <span>{row.firmwareVersion}</span>,
        hidden: isMobile,
      },
      {
        title: "Cameras",
        dataIndex: "cameraCount",
        align: "center",
        width: 80,
        hidden: isMobile && selectedAccount.accountLevel === LEVEL_TYPE.CP,
      },
    ],

    [isMobile, onClickRecorderType, selectedAccount]
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const expandableProps = useMemo<ExpandableConfig<WidgetRecorderList>>(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys,
      onExpand: (expanded: boolean, record: WidgetRecorderList) => {
        if (expanded) {
          setExpandedRowKeys([record.mergedSystemId]);
        } else {
          setExpandedRowKeys([]);
        }
      },
      expandedRowRender: (record: WidgetRecorderList) => (
        <RecorderListExpand
          mergedSystemId={record.mergedSystemId}
          columns={columns as unknown as TableColumnsType<Recorder>}
          queryInfo={recorderQueryInfo}
        />
      ),

      rowExpandable: (record: WidgetRecorderList) =>
        convertType(record) === "SPECTRUM" && record.recorderCount > 1,
    };
  }, [columns, expandedRowKeys, recorderQueryInfo]);
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    [
      "postWidgetListRecorder",
      accountIds,
      recorderQueryKey,
      selectedAccount.accountId,
    ],

    ({ pageParam = 0 }) =>
      postWidgetListRecorder({
        accountId: selectedAccount.accountId,
        payload: {
          accountIds: accountIds,
          listDto: {
            ...recorderQueryInfo,
            pageNum: pageNum === 0 ? 0 : pageParam,
          },
          type: recorderQueryInfo.type ? recorderQueryInfo.type : "",
        },
      }),
    {
      getNextPageParam: (
        lastPage: PostWidgetRecorderListResponse | undefined
      ) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onError: (e: any) => {},
      onSuccess: (res: any) => {
        const last = res.pages[res.pages.length - 1];
        if (last) {
          setRecorderQueryInfo((info) => {
            return {
              ...info,
              ...last.page,
              pageNum: last.page.pageNum + 1,
            };
          });
        }
      },
    }
  );
  const lists = useMemo(() => {
    if (pageNum === 0 && (isInitialLoading || isFetching)) return [];
    return (data?.pages || [])
      .flatMap((page: PostWidgetRecorderListResponse) => page.result ?? [])
      .map((item: WidgetRecorderList) => ({
        ...item,
        key: item.mergedSystemId,
        title: item.mergedSystemName,
        status: checkStatus(item.recorderCount, item.onlineStatusCount),
      }));
  }, [data?.pages, isFetching, isInitialLoading, pageNum]);
  const onChangeAccount = (value: string[]) => {
    setRecorderQueryInfo((quer) => {
      return {
        ...quer,
        pageNum: 0,
      };
    });
    setAccountIds(value);
  };
  // const onChangeType = (value: WidgetChart) => {
  //   setType(value);
  // };
  const onClickSearch = (keyword: string) => {
    setRecorderQueryInfo((quer) => {
      return {
        ...initialQueryInfo,
        keyword,
      };
    });
  };
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  useEffect(() => {
    if (props.layout.h >= 2) {
      setRecorderQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage * props.layout.h,
        };
      });
    } else {
      setRecorderQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage,
        };
      });
    }
  }, [props.layout.h]);

  // const isError =
  //   (type === "list" && getWidgetListRecorder.isError) ||
  //   (type === "graph" && getWidgetStatRecorder.isError);

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
        isError={isError}
      />

      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentHeader>
            <div>
              <AccountSelects onChange={onChangeAccount} value={accountIds} />
              {/* <TypeSelects onChange={onChangeType} value={type} /> */}
            </div>
            <div className="dashboard-search-input">
              <SearchInput onClickSearch={onClickSearch} />
            </div>
          </s.ContentHeader>
          <s.ContentInner>
            {/* {type === "list" && ( */}
            <DashboardChartDetailTable
              rowKey="mergedSystemId"
              data={lists}
              loadMore={() => fetchNextPage()}
              isLoading={isInitialLoading || isFetchingNextPage || isFetching}
              hasMore={
                hasNextPage &&
                !isFetchingNextPage &&
                !isInitialLoading &&
                !isFetching
              }
              columns={columns}
              expandable={expandableProps}
            />

            {/* )} */}
            {/* {type === "graph" && (
               <div className="bar-chart-wrapper">
                 {graphData.length === 0 ? (
                   <NoDataChart />
                 ) : (
                   <BarChart
                     data={graphData}
                     layout="vertical"
                     margin={{ top: 50, right: 20, bottom: 30, left: 50 }}
                     gridYValues={4}
                     enableGridY={
                       graphData.every((d) => d.barChartKey === 0) ? false : true
                     }
                     legendLabel={(value) => `${value} REC(S)`}
                     axisLeft={{
                       format: (value) => `${value} REC(S)`,
                       tickSize: 0,
                       tickValues: calTickValues(graphData),
                     }}
                     axisBottom={{
                       tickValues: getAxisBottomValue(graphData),
                       tickSize: 7, // 눈금의 길이 설정
                       tickPadding: 1, // 눈금과 레이블 간의 간격 설정
                     }}
                     isInteractive={false}
                     layers={[
                       "grid",
                       "axes",
                       "bars",
                       "markers",
                       "legends",
                       "annotations",
                     ]}
                   />
                 )}
               </div>
              )} */}
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
    </>
  );
}
